// Home gallery (SPEC-404): replaces the old topbar project popover as the project hub.
// Top: new-project presets (the four SidePanel ARTBOARD_PRESETS). Below: card grid —
// thumbnail + name + updated time, sorted by updatedAt desc. Click a card to open its canvas.
// Rename (inline input + check/cross) reuses the old ProjectSwitcher pattern; the two-step
// delete confirm is a card-level overlay (SPEC-408): question + Delete/Cancel centered over the
// dimmed card — zero layout shift, immune to narrow-card overflow.

import { useState } from 'react';
import { getLang, useT } from '../i18n';
import { CheckIcon, FrameIcon, PencilIcon, PlusIcon, XIcon } from './icons';
import { ARTBOARD_PRESETS } from './SidePanel';
import type { Project } from '../store';

export interface HomeGalleryProps {
  projects: Project[];
  onOpen: (id: string) => void;
  onNew: (artboard?: { w: number; h: number }) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
}

/** Locale-aware "updated at" stamp for the card meta row. */
function formatUpdated(ts: number): string {
  const locale = getLang() === 'zh' ? 'zh-CN' : 'en-US';
  return new Date(ts).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function HomeGallery({ projects, onOpen, onNew, onDelete, onRename }: HomeGalleryProps) {
  const t = useT();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const commitRename = () => {
    if (renameId && renameValue.trim()) onRename(renameId, renameValue);
    setRenameId(null);
  };

  const sorted = [...projects].sort((a, b) => b.updatedAt - a.updatedAt);

  return (
    <div className="home-gallery">
      <section className="gallery-section">
        <h2 className="gallery-heading">{t('home.newTitle')}</h2>
        <div className="gallery-presets">
          {ARTBOARD_PRESETS.map(p => (
            <button key={p.key} className="ghost-btn gallery-preset" onClick={() => onNew({ w: p.w, h: p.h })}>
              <PlusIcon /> {t(`preset.${p.key}`)} <span className="preset-size">{p.w}×{p.h}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="gallery-section">
        <h2 className="gallery-heading">{t('home.galleryTitle')}</h2>
        {sorted.length === 0 ? (
          <div className="gallery-empty">
            <p className="hint">{t('project.empty')}</p>
            <p className="hint">{t('home.emptyHint')}</p>
          </div>
        ) : (
          <ul className="gallery-grid">
            {sorted.map(p => (
              <li key={p.id} className="gallery-card">
                <button className="gallery-thumb-btn" onClick={() => onOpen(p.id)} aria-label={`${t('project.title')}: ${p.name}`}>
                  {p.thumb ? (
                    <img className="gallery-thumb" src={p.thumb} alt="" />
                  ) : (
                    <span className="gallery-thumb-none" aria-hidden="true"><FrameIcon /></span>
                  )}
                </button>
                <div className="gallery-card-body">
                  {renameId === p.id ? (
                    <span className="project-confirm">
                      <input
                        className="text-input project-rename-input"
                        value={renameValue}
                        autoFocus
                        maxLength={60}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                          if (e.key === 'Escape') setRenameId(null);
                        }}
                        onBlur={commitRename}
                        aria-label={t('project.rename')}
                      />
                      <button className="ghost-btn" title={t('project.rename')} onMouseDown={e => e.preventDefault()} onClick={commitRename}><CheckIcon /></button>
                      <button className="ghost-btn" title={t('project.cancel')} onMouseDown={e => e.preventDefault()} onClick={() => setRenameId(null)}><XIcon /></button>
                    </span>
                  ) : (
                    <>
                      <button className="gallery-name" onClick={() => onOpen(p.id)}>{p.name}</button>
                      <button
                        className="project-del"
                        title={t('project.rename')}
                        aria-label={`${t('project.rename')} ${p.name}`}
                        onClick={() => { setRenameValue(p.name); setRenameId(p.id); setConfirmId(null); }}
                      >
                        <PencilIcon />
                      </button>
                      <button
                        className="project-del"
                        title={t('project.delete')}
                        aria-label={`${t('project.delete')} ${p.name}`}
                        onClick={() => { setConfirmId(p.id); setRenameId(null); }}
                      >
                        <XIcon />
                      </button>
                    </>
                  )}
                </div>
                <div className="gallery-meta">{t('home.updated', { time: formatUpdated(p.updatedAt) })}</div>
                {confirmId === p.id && (
                  <div className="gallery-confirm-overlay">
                    <span className="project-confirm-text">{t('project.deleteConfirm')}</span>
                    <div className="gallery-confirm-row">
                      <button className="danger-btn" onClick={() => { onDelete(p.id); setConfirmId(null); }}>
                        {t('project.delete')}
                      </button>
                      <button className="ghost-btn" onClick={() => setConfirmId(null)}>{t('project.cancel')}</button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
